// Network side of the verifiable (PVE) server-share export; see pve-server-share.ts.

import { getMpcInstance, toBase64 } from "./mpc";
import { apiUrl } from "./apiBase";
import { sensitiveHeadersFor, sha256Hex } from "./passkey";
import type { KeyFileData } from "./crypto";
import { sealPveExport, type PveSharePayload } from "./pve-server-share";
import { throwIfExportGated } from "./server-share-export-request";
/**
 * Request a PVE export of the server share and verify it. Returns the peer-2 key file
 * (unencrypted; the caller encrypts it with the user's passphrase before saving) and
 * whether it was test-restored against the client share (`clientFile`) or only checked
 * against the server's own claim.
 */
export async function requestPveServerExport(
  keyId: string,
  /**
   * The passkey tap, given the PVE key's hash: returns the "export.pve" ticket bound to it
   * and, when PRF unlocked it, the client share used to test-restore the backup.
   */
  authorize: (ekSha256: string) => Promise<{ ticket?: string; clientFile: KeyFileData | null }>,
): Promise<{ file: KeyFileData; fullyVerified: boolean }> {
  const mpc = await getMpcInstance();
  const { ek, dk } = mpc.pveGenerateKeypair();
  try {
    const { ticket, clientFile } = await authorize(await sha256Hex(toBase64(ek)));
    const res = await fetch(apiUrl(`/api/keys/${keyId}/backup/server-share-pve`), {
      method: "POST",
      headers: await sensitiveHeadersFor(keyId, ticket),
      body: JSON.stringify({ ek: toBase64(ek) }),
    });
    if (!res.ok) await throwIfExportGated(res);
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || "Export failed");

    const fullyVerified = !!clientFile && (!data.share || !!clientFile.share) && (!data.eddsaShare || !!clientFile.eddsaShare);
    const seal = (payload: unknown, kind: "ecdsa" | "eddsa", clientShare?: string) =>
      payload ? sealPveExport(mpc, payload as PveSharePayload, ek, dk, keyId, kind, fullyVerified ? clientShare : undefined) : "";

    return {
      fullyVerified,
      file: {
        id: keyId,
        peer: 2,
        share: seal(data.share, "ecdsa", clientFile?.share),
        publicKey: data.publicKey,
        eddsaShare: seal(data.eddsaShare, "eddsa", clientFile?.eddsaShare),
        eddsaPublicKey: data.eddsaPublicKey,
        shareGeneration: typeof data.shareGeneration === "number" ? data.shareGeneration : 0,
      },
    };
  } finally {
    dk.fill(0);
  }
}
